import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import AddIcon from "@material-ui/icons/Add";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ImageIcon from "@material-ui/icons/Image";
import Search from "@material-ui/icons/Search";
import Edit from "@material-ui/icons/Edit";
import ReceiptIcon from "@material-ui/icons/Receipt";
import Close from "@material-ui/icons/Close";
import AddShoppingCart from "@material-ui/icons/AddShoppingCart";

// @material-ui/icons
import ShoppingBasket from "@material-ui/icons/ShoppingBasket";

import { useHistory } from "react-router-dom";
import EditProductDialog from "./EditProductDialog";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import CheckCircle from "@material-ui/icons/CheckCircle";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);
const useCustomStyles = makeStyles({
  searchWrap: {
    display: "flex",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "4px 10px",
    minWidth: "220px",
  },
  productNameCell: {
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  numRight: { textAlign: "right" },
  imgCell: { width: "40px", textAlign: "center", padding: "4px 8px" },
});

const useDrawerStyles = makeStyles({
  paper: { width: 560, display: "flex", flexDirection: "column" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px 12px",
  },
  body: { flex: 1, overflowY: "auto", padding: "0 20px 20px" },
  footer: { padding: "12px 20px", borderTop: "1px solid #e0e0e0" },
});

const INV_STATUS_COLORS = {
  Completed: { bg: "#e8f5e9", color: "#2e7d32" },
  Processing: { bg: "#fff8e1", color: "#f57f17" },
  Cancelled: { bg: "#fce4ec", color: "#c62828" },
  Paid: { bg: "#e3f2fd", color: "#1565c0" },
  Shipped: { bg: "#f3e5f5", color: "#6a1b9a" },
};

const PRODUCT_INVOICES_QUERY = `
  query ProductInvoices($productId: String!, $page: Int!, $pageSize: Int!) {
    invoicesWithPagination(filter: { productId: $productId, isReturn: false, page: $page, pageSize: $pageSize }) {
      data {
        id invoiceNumber invoiceDate status total paid
        customer { id name customerNumber }
      }
      total
    }
  }
`;

const GRAPHQL_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const QUERY = `
  query GetProducts($page: Int!, $pageSize: Int!, $productNumber: String, $productName: String) {
    productsWithPagination(filter: {
      page: $page, pageSize: $pageSize,
      productNumber: $productNumber, productName: $productName
    }) {
      data {
        id
        productNumber
        name
        sellingPrice
        costPrice
        onHand
        customerOrdered
        stockoutForecast
        images
      }
      total page pageSize totalPages
    }
  }
`;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatNum(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function authFetch(body) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    credentials: "include",
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

function formatStockout(raw) {
  if (!raw) return "---";
  const n = parseInt(raw, 10);
  if (isNaN(n)) return "---";
  return `${n} day(s)`;
}

function ProductImage({ images }) {
  const first = Array.isArray(images) && images[0];
  if (first) {
    return (
      <img
        src={first}
        alt=""
        style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }}
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "block";
        }}
      />
    );
  }
  return <ImageIcon style={{ fontSize: 28, color: "#90caf9" }} />;
}

export default function ProductsPage() {
  const classes = useStyles();
  const custom = useCustomStyles();
  const drawerClasses = useDrawerStyles();
  const history = useHistory();

  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");

  const [editProductId, setEditProductId] = React.useState(null);

  // Invoice drawer state
  const [invDrawer, setInvDrawer] = React.useState({
    open: false,
    product: null,
  });
  const [prodInvoices, setProdInvoices] = React.useState([]);
  const [prodInvTotal, setProdInvTotal] = React.useState(0);
  const [prodInvLoading, setProdInvLoading] = React.useState(false);

  const openInvDrawer = (product) => {
    setInvDrawer({ open: true, product });
    setProdInvoices([]);
    setProdInvTotal(0);
    setProdInvLoading(true);
    authFetch({
      query: PRODUCT_INVOICES_QUERY,
      variables: { productId: String(product.id), page: 1, pageSize: 50 },
    })
      .then(({ data }) => {
        const result = data?.invoicesWithPagination;
        setProdInvoices(result?.data || []);
        setProdInvTotal(result?.total || 0);
      })
      .finally(() => setProdInvLoading(false));
  };

  const closeInvDrawer = () => setInvDrawer({ open: false, product: null });

  const [notify, setNotify] = React.useState({ open: false, message: "" });
  const showNotify = (message) => {
    setNotify({ open: true, message });
    setTimeout(() => setNotify((n) => ({ ...n, open: false })), 4000);
  };

  const [searchNumber, setSearchNumber] = React.useState("");
  const [searchName, setSearchName] = React.useState("");
  const searchNumberRef = React.useRef("");
  const searchNameRef = React.useRef("");
  const debounceRef = React.useRef(null);

  const fetchProducts = React.useCallback((pg, ps, pNum, pName) => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: QUERY,
        variables: {
          page: pg,
          pageSize: ps,
          productNumber: pNum || undefined,
          productName: pName || undefined,
        },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
        } else {
          const p = data.productsWithPagination;
          setProducts(p.data);
          setTotal(p.total);
          setTotalPages(p.totalPages);
          setPage(p.page);
          setPageInput(String(p.page));
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    fetchProducts(1, pageSize, "", "");
  }, []);

  const triggerSearch = (pNum, pName) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProducts(1, pageSize, pNum, pName);
    }, 400);
  };

  const handleNumberChange = (e) => {
    const val = e.target.value;
    setSearchNumber(val);
    searchNumberRef.current = val;
    triggerSearch(val, searchNameRef.current);
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setSearchName(val);
    searchNameRef.current = val;
    triggerSearch(searchNumberRef.current, val);
  };

  const handlePageSizeChange = (e) => {
    const ps = e.target.value;
    setPageSize(ps);
    fetchProducts(1, ps, searchNumberRef.current, searchNameRef.current);
  };

  const goToPage = (pg) => {
    if (pg < 1 || pg > totalPages) return;
    fetchProducts(pg, pageSize, searchNumberRef.current, searchNameRef.current);
  };

  const handlePageInputKey = (e) => {
    if (e.key === "Enter") {
      const pg = parseInt(pageInput, 10);
      if (!isNaN(pg)) goToPage(pg);
    }
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = products.map((p) => [
    <span key={p.id + "-actions"} style={{ display: "flex", gap: "2px" }}>
      <IconButton
        size="small"
        title="Edit"
        onClick={() => setEditProductId(p.id)}
      >
        <Edit fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        title="View invoices"
        style={{ color: "#1976d2" }}
        onClick={() => openInvDrawer(p)}
      >
        <ReceiptIcon fontSize="small" />
      </IconButton>
    </span>,
    <ProductImage key={p.id} images={p.images} />,
    p.productNumber || "—",
    <span key={p.id + "-n"} className={custom.productNameCell} title={p.name}>
      {p.name}
    </span>,
    formatNum(p.sellingPrice),
    formatNum(p.costPrice),
    formatNum(p.onHand),
    formatNum(p.customerOrdered),
    formatStockout(p.stockoutForecast),
  ]);

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <ShoppingBasket />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Products</h4>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setEditProductId("new")}
                style={{ position: "absolute", right: "16px", top: "16px" }}
              >
                Add new product
              </Button>
            </CardHeader>
            <CardBody>
              {error && (
                <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>
              )}

              {/* Dual search */}
              <div className={custom.searchWrap}>
                <div className={custom.searchBox}>
                  <Search
                    style={{
                      color: "#aaa",
                      marginRight: "6px",
                      fontSize: "18px",
                    }}
                  />
                  <InputBase
                    placeholder="Product number..."
                    value={searchNumber}
                    onChange={handleNumberChange}
                    style={{ fontSize: "14px", minWidth: "160px" }}
                  />
                </div>
                <div className={custom.searchBox}>
                  <Search
                    style={{
                      color: "#aaa",
                      marginRight: "6px",
                      fontSize: "18px",
                    }}
                  />
                  <InputBase
                    placeholder="Product name..."
                    value={searchName}
                    onChange={handleNameChange}
                    style={{ fontSize: "14px", minWidth: "200px" }}
                  />
                </div>
              </div>

              <Table
                tableHead={[
                  "",
                  "",
                  "Product number",
                  "Product name",
                  "Selling price",
                  "Cost price",
                  "On hand",
                  "Ordered",
                  "Stockout forecast",
                ]}
                tableData={loading ? [] : tableData}
                customCellClasses={[
                  classes.center,
                  custom.imgCell,
                  classes.right,
                  classes.right,
                  classes.right,
                  classes.right,
                  classes.right,
                ]}
                customClassesForCells={[0, 1, 4, 5, 6, 7, 8]}
                customHeadCellClasses={[
                  classes.center,
                  custom.imgCell,
                  classes.right,
                  classes.right,
                  classes.right,
                  classes.right,
                  classes.right,
                ]}
                customHeadClassesForCells={[0, 1, 4, 5, 6, 7, 8]}
              />
              {loading && (
                <p style={{ textAlign: "center", padding: "16px 0" }}>
                  Loading...
                </p>
              )}

              {/* Pagination bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 0",
                  flexWrap: "wrap",
                  fontSize: "14px",
                }}
              >
                <span>Show</span>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  disableUnderline
                  style={{
                    fontSize: "14px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "2px 8px",
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt} rows
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size="small"
                  onClick={() => goToPage(1)}
                  disabled={page <= 1}
                >
                  <FirstPage fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>

                <input
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={handlePageInputKey}
                  onBlur={() => {
                    const pg = parseInt(pageInput, 10);
                    if (!isNaN(pg)) goToPage(pg);
                    else setPageInput(String(page));
                  }}
                  style={{
                    width: "48px",
                    textAlign: "center",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "4px 0",
                    fontSize: "14px",
                  }}
                />

                <IconButton
                  size="small"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => goToPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  <LastPage fontSize="small" />
                </IconButton>

                <span style={{ color: "#555" }}>
                  {from} – {to} of {total.toLocaleString("vi-VN")} products
                </span>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* ── Invoice Drawer ── */}
      <Drawer
        anchor="right"
        open={invDrawer.open}
        onClose={closeInvDrawer}
        classes={{ paper: drawerClasses.paper }}
      >
        <div className={drawerClasses.header}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "15px" }}>
              {invDrawer.product?.name || ""}
            </div>
            <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
              {invDrawer.product?.productNumber || ""}
              {!prodInvLoading &&
                ` · ${prodInvTotal} invoice${prodInvTotal !== 1 ? "s" : ""}`}
            </div>
          </div>
          <IconButton size="small" onClick={closeInvDrawer}>
            <Close fontSize="small" />
          </IconButton>
        </div>
        <Divider />

        <div className={drawerClasses.body}>
          {prodInvLoading && (
            <p
              style={{ textAlign: "center", padding: "24px 0", color: "#888" }}
            >
              Loading...
            </p>
          )}
          {!prodInvLoading && prodInvoices.length === 0 && (
            <p
              style={{ textAlign: "center", padding: "24px 0", color: "#aaa" }}
            >
              No invoices found for this product.
            </p>
          )}
          {!prodInvLoading && prodInvoices.length > 0 && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                marginTop: "8px",
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: "2px solid #f0f0f0",
                    color: "#888",
                    textAlign: "left",
                  }}
                >
                  <th style={{ padding: "6px 4px" }}>Invoice</th>
                  <th style={{ padding: "6px 4px" }}>Date</th>
                  <th style={{ padding: "6px 4px" }}>Customer</th>
                  <th style={{ padding: "6px 4px" }}>Status</th>
                  <th style={{ padding: "6px 4px", textAlign: "right" }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {prodInvoices.map((inv) => {
                  const sc = INV_STATUS_COLORS[inv.status] || {
                    bg: "#f5f5f5",
                    color: "#555",
                  };
                  const debt = (inv.total || 0) - (inv.paid || 0);
                  return (
                    <tr
                      key={inv.id}
                      style={{ borderBottom: "1px solid #f5f5f5" }}
                    >
                      <td style={{ padding: "7px 4px" }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#1976d2",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => {
                            history.push(`/admin/invoices/${inv.id}`);
                            closeInvDrawer();
                          }}
                        >
                          {inv.invoiceNumber}
                        </span>
                      </td>
                      <td style={{ padding: "7px 4px", color: "#555" }}>
                        {formatDate(inv.invoiceDate)}
                      </td>
                      <td style={{ padding: "7px 4px" }}>
                        {inv.customer ? (
                          <span>
                            <span style={{ fontSize: "11px", color: "#888" }}>
                              {inv.customer.customerNumber}{" "}
                            </span>
                            {inv.customer.name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ padding: "7px 4px" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: "10px",
                            backgroundColor: sc.bg,
                            color: sc.color,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {inv.status || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 4px", textAlign: "right" }}>
                        <span style={{ fontWeight: 600 }}>
                          {formatNum(inv.total)}
                        </span>
                        {debt > 0 && (
                          <div style={{ fontSize: "11px", color: "#e53935" }}>
                            owe {formatNum(debt)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <Divider />
        <div className={drawerClasses.footer}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddShoppingCart />}
            onClick={() => {
              const p = invDrawer.product;
              history.push(
                `/admin/invoices/new?productId=${
                  p.id
                }&productNumber=${encodeURIComponent(
                  p.productNumber || ""
                )}&productName=${encodeURIComponent(p.name || "")}`
              );
              closeInvDrawer();
            }}
          >
            Create Invoice
          </Button>
        </div>
      </Drawer>

      <EditProductDialog
        productId={editProductId}
        onClose={() => setEditProductId(null)}
        onSaved={(msg) => {
          showNotify(msg || "Product saved successfully!");
          fetchProducts(
            page,
            pageSize,
            searchNumberRef.current,
            searchNameRef.current
          );
        }}
      />
      <Snackbar
        place="tr"
        color="success"
        icon={CheckCircle}
        message={notify.message}
        open={notify.open}
        closeNotification={() => setNotify((n) => ({ ...n, open: false }))}
        close
      />
    </>
  );
}
